// Minimal in-browser signature pad. HTML5 canvas + pointer events;
// no third-party deps. The merchant draws with mouse or finger, the
// component fires onChange with the canvas exported as a base64 PNG
// data URL on each stroke end. "Clear" wipes the canvas and fires
// onChange with an empty string.
//
// Used in MnspEnrollmentSection to capture the merchant's real
// written signature for the MAR + EUSA packet. The captured
// data URL persists on MnspEnrollment.signatureImage and gets
// stamped into the rendered PDF via pdf_render's overlay step.

import { useEffect, useRef, useState } from "react";

interface Props {
  /** Existing signature data URL (e.g. on form reload). */
  value?: string | null;
  /** Fires on stroke end with the current canvas as a data URL,
   *  or empty string when cleared. */
  onChange: (dataUrl: string) => void;
  /** Canvas pixel dimensions. Defaults to 400x120 (~3.3:1 ratio,
   *  matches the typical signature-line aspect on a PDF). */
  width?: number;
  height?: number;
  /** Accessibility label for the canvas. */
  ariaLabel?: string;
  printedNameFallback?: string | null;
}

export function SignaturePad({
  value,
  onChange,
  width = 400,
  height = 120,
  ariaLabel = "Signature pad",
  printedNameFallback = null,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  // The last dataURL (or "") this component emitted via onChange. When the
  // parent echoes it straight back as `value`, the rehydrate effect uses
  // this to skip a redundant clear+redraw.
  const lastEmittedRef = useRef<string | null>(null);
  const [hasInk, setHasInk] = useState(!!value);
  const [announcement, setAnnouncement] = useState("");
  const [mode, setMode] = useState<"draw" | "typed">("draw");

  // Re-hydrate from `value` on mount + when value changes externally.
  // Useful when the form reloads with a previously-saved signature.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    // Skip when `value` is just the echo of what we last emitted: the canvas
    // already shows it, so a clear+redraw here would flicker and, on a slow
    // device, could wipe an in-progress stroke that started before the
    // parent's state update round-tripped back. The ctx keeps its stroke
    // styles from the initial run, so nothing is lost by returning early.
    if (value === lastEmittedRef.current) return;
    // Leave the canvas transparent (no white fill) so the exported PNG
    // has an alpha background. The signature then composites cleanly
    // onto the MAR PDF (pdf_render draws it with mask="auto") instead of
    // painting a white box over the form line. The on-screen white
    // drawing surface comes from the container div's bg-white, so dark
    // ink stays visible against the dark team-portal UI while drawing.
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#0A1C24";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    if (value) {
      let cancelled = false;
      const img = new Image();
      img.onload = () => {
        // A newer value (or unmount) landed while the image was decoding;
        // don't draw a stale signature over the current canvas.
        if (cancelled) return;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        setHasInk(true);
      };
      img.src = value;
      return () => {
        cancelled = true;
        img.onload = null;
      };
    } else {
      setHasInk(false);
    }
  }, [value]);

  useEffect(() => {
    if (mode !== "typed") return;

    const name = printedNameFallback?.trim();
    if (!name) {
      clearCanvas();
      setHasInk(false);
      lastEmittedRef.current = "";
      onChange("");

      return;
    }

    // Canvas fillText neither triggers a font download nor waits for one:
    // it paints once with whatever glyphs exist at that instant and never
    // repaints when the font arrives. Pacifico is only referenced from the
    // canvas (never in DOM text), so on the first typed-mode selection it
    // usually isn't loaded yet and the name renders in a fallback font --
    // reselecting worked only because the face had finished loading by then.
    // Load the face first, then draw. Guard against a mode change/unmount
    // landing while the load promise is in flight.
    const FONT = '42px "Pacifico"';
    if (document.fonts && !document.fonts.check(FONT)) {
      let cancelled = false;
      document.fonts
        .load(FONT)
        .catch(() => {})
        .then(() => {
          if (!cancelled) renderTypedSignature(name);
        });
      return () => {
        cancelled = true;
      };
    }

    renderTypedSignature(name);
  }, [mode, printedNameFallback]);

  function pointFromEvent(e: React.PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    // Map clientX/Y to canvas-space coords, scaling for any CSS
    // scaling between the canvas's logical pixel size and its
    // displayed size on the page.
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }

  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    if (mode === "typed") return;
    e.preventDefault();
    const canvas = canvasRef.current!;
    canvas.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    lastPointRef.current = pointFromEvent(e);
  }

  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current || mode === "typed") return;
    e.preventDefault();
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const next = pointFromEvent(e);
    const prev = lastPointRef.current ?? next;
    ctx.beginPath();
    ctx.moveTo(prev.x, prev.y);
    ctx.lineTo(next.x, next.y);
    ctx.stroke();
    lastPointRef.current = next;
    if (!hasInk) setHasInk(true);
  }

  function endStroke(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current || mode === "typed") return;
    drawingRef.current = false;
    lastPointRef.current = null;
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      canvas.releasePointerCapture(e.pointerId);
    } catch {
      // Some browsers throw if capture wasn't set; ignore.
    }
    const dataUrl = canvas.toDataURL("image/png");
    lastEmittedRef.current = dataUrl;
    onChange(dataUrl);
  }

  function clearCanvas() {
    // Clear to transparent (not white), matching the init effect.
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }

  function clear() {
    clearCanvas();
    setHasInk(false);
    lastEmittedRef.current = "";
    onChange("");
    setAnnouncement("Signature cleared");

    if (mode === "typed") {
      setMode("draw");
    }
  }

  function renderTypedSignature(name: string) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Leave the background transparent for the same reason the draw path
    // does: the exported PNG composites onto the MAR PDF rather than
    // painting a white box over the form line.
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#0A1C24";
    ctx.textBaseline = "middle";

    // Shrink to fit. At a fixed size a long printed name runs off the
    // canvas and the export silently clips it -- and this image is what
    // ends up on the signed MAR PDF, so a clipped name is a real problem.
    // Step down until it fits, with a floor so it stays legible; the
    // fillText maxWidth is a final backstop for names still too long at
    // the floor size.
    const padding = 12;
    const maxWidth = canvas.width - padding * 2;
    let fontSize = 42;
    const MIN_FONT_PX = 16;
    for (;;) {
      ctx.font = `${fontSize}px "Pacifico", cursive`;
      if (ctx.measureText(name).width <= maxWidth) break;
      if (fontSize <= MIN_FONT_PX) break;
      fontSize -= 2;
    }
    ctx.fillText(name, padding, canvas.height / 2, maxWidth);

    const dataUrl = canvas.toDataURL("image/png");
    // Mirror the draw/clear paths: record what we emitted so the rehydrate
    // effect skips a redundant clear+redraw when the parent echoes `value`
    // straight back.
    lastEmittedRef.current = dataUrl;
    setHasInk(true);
    onChange(dataUrl);
    setAnnouncement("Using typed signature");
  }

  return (
    <div>
      <div className="rounded-md border border-brand-border bg-white">
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          aria-label={ariaLabel}
          aria-describedby="signature-help"
          role="img"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endStroke}
          onPointerCancel={endStroke}
          onPointerLeave={endStroke}
          // Crucial for mobile -- prevents scroll-on-touch from
          // hijacking the drawing gesture.
          style={{ touchAction: "none", display: "block", width: "100%" }}
        />
      </div>
      <div className="flex items-start gap-2 pt-4 pb-4">
        {/* htmlFor/id pairing so clicking the label text toggles the box and
            screen readers get the label as the control's accessible name.
            The visible <label> supplies that name, so no aria-label is
            needed here, and the agreement line is associated as a
            description rather than carrying an aria-label of its own (a
            non-interactive <p> gains nothing from one). */}
        <input
          id="signature-typed-toggle"
          className="mt-1 size-4 cursor-pointer accent-brand-blue"
          type="checkbox"
          checked={mode === "typed"}
          onChange={(e) => {
            setMode(e.target.checked ? "typed" : "draw");
            if (!e.target.checked) {
              clear();
            }
          }}
          aria-describedby="signature-typed-help"
        />
        <div className="flex-1 space-y-1">
          <label
            htmlFor="signature-typed-toggle"
            className="block cursor-pointer text-sm font-medium text-brand-text"
          >
            Use typed signature
          </label>
          <p id="signature-typed-help" className="text-xs text-brand-muted">
            You agree to using a typed signature when selecting this option
          </p>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-brand-muted">
        {/* Typed mode with no printed name yet renders nothing, so without
            this the pad just says "sign with your mouse or finger" and the
            submit gate complains about a missing signature with no hint
            that the printed name is what's actually missing. */}
        <span id="signature-help">
          {hasInk
            ? "Signature captured"
            : mode === "typed"
              ? "Add your printed name above and it will be used as your signature"
              : `Sign with your mouse or finger, or select the "Use typed signature" option`}
        </span>
        <button
          type="button"
          aria-label="Clear signature"
          onClick={clear}
          className="text-brand-blue underline-offset-2 hover:underline"
          disabled={!hasInk}
        >
          Clear
        </button>
      </div>
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>
    </div>
  );
}
